// packages/console/src/components/panels/LiveIngestPanel.tsx
//
// Ingest panel for the LIVE console, ported from the Bonded-source block of
// docs/design/ui_kits/aether-live/Transcoder.jsx. It reads REAL data off the
// live SSE stream (LiveStream + LiveLink), not a sim tick: the bonded input
// rate and link RTT as readouts, one row per bonded WAN link (per-link bitrate,
// a Meter, and its carrier), then a source spec block.
//
// Honest absence (R10): when the session is stopped or a figure is absent, the
// stream sends null and every readout falls to an em-dash. The link Meter reads
// 0 for a down/idle link; a down link's Dot idles.

import { useState, type CSSProperties } from "react";
import { Panel, Read, Row, Eb } from "../Parts";
import { Icon } from "../Icons";
import type { LiveStream } from "../../api/live";
import type { LiveTransport } from "../../api/liveControl";
import { EM_DASH, fmtMbps, fmtNum } from "../../lib/format";

// TRANSPORT_OPTIONS are the RTSP pull transports the operator can pin. "auto"
// probes TCP first and flips to UDP if TCP delivers no media; "tcp"/"udp" force
// one with no flip. The labels stay terse; the tooltip carries the meaning.
const TRANSPORT_OPTIONS: { value: LiveTransport; label: string; hint: string }[] = [
  { value: "auto", label: "Auto", hint: "Try TCP first, fall back to UDP if no media arrives" },
  { value: "tcp", label: "TCP", hint: "Force RTSP-interleaved TCP (NAT-friendly)" },
  { value: "udp", label: "UDP", hint: "Force RTSP over UDP (what VLC uses)" },
];

// isRtspPull reports whether a (redacted) source URL is an RTSP pull, the only
// case where the transport selector is meaningful. RTMP/SRT/HTTP pulls ignore it.
function isRtspPull(sourceUrl: string): boolean {
  return /^rtsps?:/i.test(sourceUrl.trim());
}

// TransportField is a three-way segmented selector for the RTSP pull transport.
// The change persists (onSelect) and takes effect at the next Go-live; a server
// rejection surfaces inline. Rendered only for an RTSP pull source.
function TransportField({
  value,
  onSelect,
  busy,
}: {
  value: LiveTransport;
  onSelect: (t: LiveTransport) => Promise<unknown>;
  busy?: boolean;
}) {
  const [err, setErr] = useState<string | null>(null);
  const pick = async (t: LiveTransport) => {
    if (t === value) return;
    try {
      await onSelect(t);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "could not update the transport");
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ font: "var(--t-micro)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--fg4)" }}>
        RTSP transport
      </span>
      <div style={{ display: "inline-flex", gap: 4 }}>
        {TRANSPORT_OPTIONS.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              onClick={() => void pick(opt.value)}
              disabled={busy}
              title={opt.hint}
              style={{
                flex: 1,
                padding: "6px 10px",
                borderRadius: "var(--r-sm)",
                border: `1px solid ${active ? "var(--blue-500)" : "var(--line-strong)"}`,
                background: active ? "var(--blue-500-a15, transparent)" : "transparent",
                color: active ? "var(--blue-400)" : "var(--fg3)",
                font: "var(--t-micro)",
                cursor: busy ? "default" : "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {err && <span style={{ font: "var(--t-micro)", color: "var(--err)" }}>{err}</span>}
    </div>
  );
}

// CopyField renders a read-only mono value with a copy affordance so the
// broadcaster lifts the SRT ingest URL and stream key straight into their
// encoder. It shows a brief "copied" confirmation and falls back silently when
// the clipboard API is unavailable (embedded webviews), never faking success.
function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => {});
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ font: "var(--t-micro)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--fg4)" }}>
        {label}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          title={value}
          style={{
            flex: 1,
            font: "400 12px var(--font-mono)",
            color: "var(--fg2)",
            padding: "7px 9px",
            background: "var(--bg-input)",
            border: "1px solid var(--line)",
            borderRadius: "var(--r-sm)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </span>
        <button
          onClick={copy}
          title={`Copy ${label.toLowerCase()}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 7px",
            borderRadius: "var(--r-sm)",
            border: "1px solid var(--line-strong)",
            background: "transparent",
            color: copied ? "var(--blue-400)" : "var(--fg3)",
            font: "var(--t-micro)",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {copied && <Icon name="check" size={11} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

// KEY_PATTERN mirrors the server's validation (letters, digits, dot, dash,
// underscore, 3..128 chars) so an invalid key is caught inline before the POST.
const KEY_PATTERN = /^[A-Za-z0-9._-]{3,128}$/;

// EditableKeyField shows the stream key with an Edit affordance. Editing persists
// the key (onSave), so a broadcaster can present a memorable key; the SRT URL
// above rebuilds from the returned record. A local charset check gates the save,
// and a server rejection (e.g. a key already in use) surfaces inline.
function EditableKeyField({
  value,
  onSave,
  busy,
}: {
  value: string;
  onSave: (key: string) => Promise<unknown>;
  busy?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      })
      .catch(() => {});
  };
  const begin = () => {
    setDraft(value);
    setErr(null);
    setEditing(true);
  };
  const save = async () => {
    const key = draft.trim();
    if (!KEY_PATTERN.test(key)) {
      setErr("3 to 128 chars: letters, digits, dot, dash, underscore.");
      return;
    }
    try {
      await onSave(key);
      setEditing(false);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "could not update the key");
    }
  };

  const btn: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 7px",
    borderRadius: "var(--r-sm)",
    border: "1px solid var(--line-strong)",
    background: "transparent",
    color: "var(--fg3)",
    font: "var(--t-micro)",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ font: "var(--t-micro)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--fg4)" }}>
        Stream key
      </span>
      {editing ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              value={draft}
              autoFocus
              spellCheck={false}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void save();
                if (e.key === "Escape") setEditing(false);
              }}
              style={{
                flex: 1,
                font: "400 11px var(--font-mono)",
                color: "var(--fg1)",
                background: "var(--bg-input)",
                border: `1px solid ${err ? "var(--err)" : "var(--line-strong)"}`,
                borderRadius: "var(--r-sm)",
                padding: "7px 9px",
                minWidth: 0,
              }}
            />
            <button onClick={() => void save()} disabled={busy} title="Save the stream key" style={{ ...btn, color: "var(--blue-400)", borderColor: "var(--blue-500)" }}>
              Save
            </button>
            <button onClick={() => setEditing(false)} title="Cancel" style={btn}>
              Cancel
            </button>
          </div>
          {err && <span style={{ font: "var(--t-micro)", color: "var(--err)" }}>{err}</span>}
        </>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            title={value}
            style={{
              flex: 1,
              font: "400 12px var(--font-mono)",
              color: "var(--fg2)",
              padding: "7px 9px",
              background: "var(--bg-input)",
              border: "1px solid var(--line)",
              borderRadius: "var(--r-sm)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </span>
          <button onClick={copy} title="Copy stream key" style={{ ...btn, color: copied ? "var(--blue-400)" : "var(--fg3)" }}>
            {copied && <Icon name="check" size={11} />}
            {copied ? "Copied" : "Copy"}
          </button>
          <button onClick={begin} title="Set a custom stream key" style={btn}>
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

// EditableSourceField shows the stream's PULL source URL with an Edit affordance.
// Setting a URL makes the stream pull that source on Go-live (instead of waiting
// for a push); clearing it returns to push mode. The server validates the scheme
// (and, at Go-live, that the host is public) and returns the URL credential-
// redacted, so the field never echoes a camera password. A server rejection
// surfaces inline.
function EditableSourceField({
  value,
  onSave,
  busy,
}: {
  value: string;
  onSave: (url: string) => Promise<unknown>;
  busy?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [err, setErr] = useState<string | null>(null);

  const begin = () => {
    setDraft(value);
    setErr(null);
    setEditing(true);
  };
  const commit = async (next: string) => {
    try {
      await onSave(next.trim());
      setEditing(false);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "could not update the source");
    }
  };

  const btn: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 7px",
    borderRadius: "var(--r-sm)",
    border: "1px solid var(--line-strong)",
    background: "transparent",
    color: "var(--fg3)",
    font: "var(--t-micro)",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ font: "var(--t-micro)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--fg4)" }}>
        Pull source (optional)
      </span>
      {editing ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              value={draft}
              autoFocus
              spellCheck={false}
              placeholder="rtsp://user:pass@host:554/stream"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void commit(draft);
                if (e.key === "Escape") setEditing(false);
              }}
              style={{
                flex: 1,
                font: "400 11px var(--font-mono)",
                color: "var(--fg1)",
                background: "var(--bg-input)",
                border: `1px solid ${err ? "var(--err)" : "var(--line-strong)"}`,
                borderRadius: "var(--r-sm)",
                padding: "7px 9px",
                minWidth: 0,
              }}
            />
            <button onClick={() => void commit(draft)} disabled={busy} title="Save the pull source" style={{ ...btn, color: "var(--blue-400)", borderColor: "var(--blue-500)" }}>
              Save
            </button>
            <button onClick={() => setEditing(false)} title="Cancel" style={btn}>
              Cancel
            </button>
          </div>
          {err && <span style={{ font: "var(--t-micro)", color: "var(--err)" }}>{err}</span>}
        </>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            title={value || "No pull source, this stream waits for a push"}
            style={{
              flex: 1,
              font: "400 12px var(--font-mono)",
              color: value ? "var(--fg2)" : "var(--fg4)",
              padding: "7px 9px",
              background: "var(--bg-input)",
              border: "1px solid var(--line)",
              borderRadius: "var(--r-sm)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value || "None (push mode)"}
          </span>
          {value && (
            <button onClick={() => void commit("")} disabled={busy} title="Clear the pull source (back to push mode)" style={btn}>
              Clear
            </button>
          )}
          <button onClick={begin} title="Set a pull source URL" style={btn}>
            {value ? "Edit" : "Set"}
          </button>
        </div>
      )}
    </div>
  );
}

export function LiveIngestPanel({
  stream,
  ingestUrl,
  streamKey,
  onSetKey,
  sourceUrl,
  onSetSource,
  transport,
  onSetTransport,
  busy,
}: {
  stream: LiveStream | null;
  // The provisioned stream's ingest endpoint. Absent until a stream is created.
  ingestUrl?: string | null;
  streamKey?: string | null;
  // onSetKey persists a custom stream key for the current stream. Absent (the key
  // stays read-only) until a stream is provisioned.
  onSetKey?: (key: string) => Promise<unknown>;
  // sourceUrl is the current stream's pull source (redacted); onSetSource persists
  // a new one (or clears it). Both absent until a stream is provisioned.
  sourceUrl?: string | null;
  onSetSource?: (url: string) => Promise<unknown>;
  // transport is the current stream's RTSP pull transport (auto/tcp/udp);
  // onSetTransport persists a new one. The selector renders only for an RTSP pull.
  transport?: LiveTransport | null;
  onSetTransport?: (t: LiveTransport) => Promise<unknown>;
  busy?: boolean;
}) {
  return (
    <Panel
      title="Ingest"
      right={<span style={{ font: "400 10px var(--font-mono)", color: "var(--fg3)" }}>SRT :9000</span>}
    >
      <Eb style={{ marginBottom: 9 }}>Ingest endpoint</Eb>
      {ingestUrl && streamKey ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 16 }}>
          <CopyField label="SRT URL" value={ingestUrl} />
          {onSetKey ? (
            <EditableKeyField value={streamKey} onSave={onSetKey} busy={busy} />
          ) : (
            <CopyField label="Stream key" value={streamKey} />
          )}
          {onSetSource && <EditableSourceField value={sourceUrl ?? ""} onSave={onSetSource} busy={busy} />}
          {onSetTransport && isRtspPull(sourceUrl ?? "") && (
            <TransportField value={transport ?? "auto"} onSelect={onSetTransport} busy={busy} />
          )}
        </div>
      ) : (
        <div style={{ font: "var(--t-micro)", color: "var(--fg4)", marginBottom: 14 }}>
          Create a stream to get an ingest URL
        </div>
      )}
      <div style={{ height: 1, background: "var(--line)", margin: "0 0 12px" }} />
      {/* Source readouts that are real for a pull/push ingest: the measured input
          bitrate and the detected source cadence. The bonded-WAN link meters from
          the prototype are dropped -- this ingest is a single source, not a bond. */}
      <Eb style={{ marginBottom: 9 }}>Source</Eb>
      <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
        <Read value={fmtMbps(stream?.ingestBitsPerSec ?? null)} unit="Mb/s" label="Input rate" />
      </div>
      <Row label="Stream" value={sourceSpec(stream)} />
      {/* Detected source stream properties, read off the decode banner (no second
          connection). Each is absent until the decode reports it -> em-dash. Audio
          stays an em-dash for a video-only source. */}
      <Row label="Resolution" value={sourceStrSpec(stream?.sourceResolution ?? null)} />
      <Row label="Video codec" value={sourceStrSpec(stream?.sourceVideoCodec ?? null)} />
      <Row label="Audio codec" value={sourceStrSpec(stream?.sourceAudioCodec ?? null)} />
      {/* Detected source cadence (fps). This is what a "Match source" rung
          inherits. Absent until the source is probed -> em-dash (honest absence). */}
      <Row label="Source fps" value={sourceFpsSpec(stream)} />
    </Panel>
  );
}

// sourceSpec renders the stream name, or an em-dash when there is no session.
function sourceSpec(stream: LiveStream | null): string {
  return stream && stream.name ? stream.name : EM_DASH;
}

// sourceStrSpec renders a detected source property string, or an em-dash when it
// is not (yet) known (no session, not parsed, or genuinely absent like the audio
// codec of a video-only source).
function sourceStrSpec(value: string | null): string {
  return value && value.trim() !== "" ? value : EM_DASH;
}

// sourceFpsSpec renders the detected source frame rate as "<n> fps", or an
// em-dash when the cadence is not yet known (no probe, or no session). This is
// the cadence a "Match source" rung inherits (frameRate = 0 in the preset).
function sourceFpsSpec(stream: LiveStream | null): string {
  const fps = stream?.sourceFps ?? null;
  if (fps === null) return EM_DASH;
  return `${fmtNum(fps, 0)} fps`;
}
